import { useState, useMemo } from 'react'
import {
  Box,
  Badge,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useContextState } from '../../../../../../hooks/theme'
import { icons } from '../../../../../../utils/icons'
import { SnackSimple } from '../../../../../../components'
import { useBody } from './body'
import { useStyles } from './styles'
import { RedirectedIds } from '../../../../typing'
import MultiSelectSimple from '../../../../../../components/dinamicForm/multiSelectSimple'
import { SelectionList } from './components/SelectionList'
import { FilterModal } from './components/FilterModal'
import { QueryResults } from './components/QueryResults'
import { VersionSelectionPanel } from './components/VersionSelectionPanel'
import { StorePerformanceProvider } from './context/StorePerformanceContext'

const StorePerformance = () => {
  const { t } = useTranslation()
  const styles = useStyles()
  const { colors } = useContextState()
  const [filterModalOpen, setFilterModalOpen] = useState(false)

  const {
    queryMode,
    handleQueryModeChange,
    selectedCompany,
    setSelectedCompany,
    selectedUnits,
    selectedQuestionnaires,
    searchUnit,
    setSearchUnit,
    searchQuestionnaire,
    setSearchQuestionnaire,
    hasQueried,
    filteredUnits,
    filteredQuestionnaires,
    canConsult,
    isUnitSelected,
    isQuestionnaireSelected,
    toggleUnit,
    toggleQuestionnaire,
    areAllUnitsSelected,
    areAllQuestionnairesSelected,
    toggleAllUnits,
    toggleAllQuestionnaires,
    unitSelectionType,
    questionnaireSelectionType,
    selectedQuestionnairesWithVersions,
    hasSelectedQuestionnaireWithVersions,
    showVersionsPanel,
    activeVersionQuestionnaireId,
    selectedVersionsByQuestionnaire,
    openVersionsPanel,
    setActiveVersionQuestionnaire,
    toggleVersionSelection,
    toggleAllVersionsForQuestionnaire,
    canViewResults,
    handleCleanFilters,
    handleConsult,
    handleDownload,
    handleNavigateToProgramation,
    companiesList,
    companiesLoading,
    dataLoading,
    hasAssignedCompany,
    periodFrom,
    setPeriodFrom,
    periodTo,
    setPeriodTo,
    periodWeek,
    setPeriodWeek,
    periodYear,
    setPeriodYear,
    periodValidationError,
    hasPeriodErrors,
    filterDivision,
    setFilterDivision,
    filterZone,
    setFilterZone,
    filterUser,
    setFilterUser,
    filterRole,
    setFilterRole,
    divisionsOptions,
    zonesOptions,
    usersOptions,
    rolesOptions,
    filtersLoading,
    isPartner,
    hideZone,
    handleApplyFilters,
    filterActiveCount,
    displayPeriod,
    snackbar,
    closeSnackbar,
    resultsData,
    resultsLoading,
    lastConsultTimeFormatted,
    activeTab,
    handleTabChange,
    chartType,
    setChartType,
    selectedResultIds,
    toggleResultItem,
    selectAllResultItems,
    searchResult,
    setSearchResult,
    filteredResultItems,
    resultsPage,
    setResultsPage,
    totalPages,
    paginatedResultItems,
    performanceData,
    chartData,
    chartSeries,
    globalAverageScore,
    chartColors,
    filteredPieChartData,
    pieChartLoading,
    pieChartPagination,
    handleChartTypeChange,
    handlePieChartPageChange,
    processStatusQueryParams,
    restoredFromNavigation,
    downloadPopoverOpen,
    downloadPopoverAnchor,
    closeDownloadPopover,
    downloadingPdf,
    downloadingExcel,
    handleDownloadPdf,
    handleDownloadExcel,
    handleRangeApplied,
  } = useBody()

  const scoreMap = useMemo(() => {
    const map = new Map<string, number>()
    performanceData.forEach((item) => map.set(item.id, item.score))
    return map
  }, [performanceData])

  const resultListTitle = queryMode === 'many-units-one-questionnaire'
    ? t('dashboard.storePerformance.results.units')
    : t('dashboard.storePerformance.results.questionnaires')

  return (
    <StorePerformanceProvider>
      <Grid item xs={12}>
        <div id={RedirectedIds.STORE_PERFORMANCE} />
        <Box sx={styles.container}>
        {/* Header interno */}
        <Box sx={styles.headerSection}>
          <Box sx={styles.titleGroup}>
            <Typography sx={styles.title}>
              {t('dashboard.storePerformance.title')}
            </Typography>
            <Typography sx={styles.subtitle}>
              {t('dashboard.storePerformance.selectedPeriod')}:{' '}
              <span>{displayPeriod}</span>
            </Typography>
          </Box>

          <Box sx={styles.actionsGroup}>
            <Button 
              sx={styles.textButton} 
              onClick={handleDownload}
              disabled={!hasQueried || Object.keys(resultsData).length === 0}
            >
              {t('dashboard.storePerformance.download')}
            </Button>
            <Button
              sx={{
                ...styles.textButton,
                color: colors.color31,
                '&:hover': {
                  backgroundColor: colors.color14,
                },
              }}
              onClick={handleCleanFilters}
            >
              {t('dashboard.storePerformance.cleanFilters')}
            </Button>
            <Badge
              badgeContent={filterActiveCount}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#000',
                  color: '#fff',
                },
              }}
            >
              <IconButton sx={styles.iconButton} onClick={() => setFilterModalOpen(true)}>
                <Box sx={{ fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {icons('filter')}
                </Box>
              </IconButton>
            </Badge>
            <IconButton
              sx={{
                ...styles.searchIconButton,
                backgroundColor: colors.color45,
                color: '#fff',
                '&:hover': {
                  backgroundColor: `${colors.color45}dd`,
                },
                '&:disabled': {
                  backgroundColor: colors.color10,
                  color: colors.color5,
                },
              }}
              onClick={handleNavigateToProgramation}
              disabled={!hasQueried || Object.keys(resultsData).length === 0}
            >
              {icons('search')}
            </IconButton>
          </Box>
        </Box>

        {/* Configure query section */}
        <Box sx={styles.section}>
          <Typography sx={styles.cardTitle}>
            {t('dashboard.storePerformance.configureQuery')}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px', mb: '16px' }}>
            <RadioGroup
              row
              value={queryMode}
              onChange={(e) => handleQueryModeChange(e.target.value as typeof queryMode)}
              sx={styles.radioGroupRow}
            >
              <Box sx={queryMode === 'one-unit-many-questionnaires' ? styles.radioChecked : undefined}>
                <FormControlLabel
                  value="one-unit-many-questionnaires"
                  control={<Radio size="small" />}
                  label={t('dashboard.storePerformance.queryMode.oneUnitManyQ')}
                  sx={queryMode === 'one-unit-many-questionnaires' ? { ...styles.radioLabel, '& .MuiFormControlLabel-label': { fontSize: '13px', color: colors.color16 } } : styles.radioLabel}
                />
              </Box>
              <Box sx={queryMode === 'many-units-one-questionnaire' ? styles.radioChecked : undefined}>
                <FormControlLabel
                  value="many-units-one-questionnaire"
                  control={<Radio size="small" />}
                  label={t('dashboard.storePerformance.queryMode.manyUnitsOneQ')}
                  sx={queryMode === 'many-units-one-questionnaire' ? { ...styles.radioLabel, '& .MuiFormControlLabel-label': { fontSize: '13px', color: colors.color16 } } : styles.radioLabel}
                />
              </Box>
              <Box sx={queryMode === 'one-unit-one-questionnaire' ? styles.radioChecked : undefined}>
                <FormControlLabel
                  value="one-unit-one-questionnaire"
                  control={<Radio size="small" />}
                  label={t('dashboard.storePerformance.queryMode.oneUnitOneQ')}
                  sx={queryMode === 'one-unit-one-questionnaire' ? { ...styles.radioLabel, '& .MuiFormControlLabel-label': { fontSize: '13px', color: colors.color16 } } : styles.radioLabel}
                />
              </Box>
            </RadioGroup>

            {!hasAssignedCompany && (
              <MultiSelectSimple
                name="company"
                label={t('dashboard.storePerformance.selectCompany')}
                items={companiesList}
                selected={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value ?? null)}
                loading={companiesLoading}
                disabled={companiesLoading}
                sx={styles.companySelect}
              />
            )}
          </Box>

          <Grid container spacing={2}>
            {/* Units column */}
            <Grid item xs={12} md={6}>
              <SelectionList
                title={t('dashboard.storePerformance.units')}
                selectedCount={selectedUnits.length}
                selectedCountLabel={t('dashboard.storePerformance.selectedCount', { count: selectedUnits.length })}
                searchValue={searchUnit}
                onSearchChange={setSearchUnit}
                data={filteredUnits}
                selectedItems={selectedUnits}
                isSelected={isUnitSelected}
                toggle={toggleUnit}
                showSelectAll={queryMode === 'many-units-one-questionnaire'}
                areAllSelected={areAllUnitsSelected}
                toggleAll={toggleAllUnits}
                loading={dataLoading}
                disabled={!hasAssignedCompany && !selectedCompany}
                emptyMessage={t('dashboard.storePerformance.selectCompanyFirst')}
                searchPlaceholder={t('dashboard.storePerformance.search')}
                selectAllLabel={t('dashboard.storePerformance.selectAll')}
                icons={icons}
                selectionType={unitSelectionType}
                styles={{
                  listColumn: styles.listColumn,
                  listColumnHeader: styles.listColumnHeader,
                  listColumnCounter: styles.listColumnCounter,
                  searchField: styles.searchField,
                  checkboxRow: styles.checkboxRow,
                  checkboxRowAll: styles.checkboxRowAll,
                }}
                colors={{ color9: colors.color9, color13: colors.color13 }}
                t={t}
              />
            </Grid>

            {/* Questionnaires column */}
            <Grid item xs={12} md={6}>
              <SelectionList
                title={t('dashboard.storePerformance.questionnaires')}
                selectedCount={selectedQuestionnaires.length}
                selectedCountLabel={t('dashboard.storePerformance.selectedCountQuestionnaire', { count: selectedQuestionnaires.length })}
                searchValue={searchQuestionnaire}
                onSearchChange={setSearchQuestionnaire}
                data={filteredQuestionnaires}
                selectedItems={selectedQuestionnaires}
                isSelected={isQuestionnaireSelected}
                toggle={toggleQuestionnaire}
                showSelectAll={queryMode === 'one-unit-many-questionnaires'}
                areAllSelected={areAllQuestionnairesSelected}
                toggleAll={toggleAllQuestionnaires}
                loading={dataLoading}
                disabled={!hasAssignedCompany && !selectedCompany}
                emptyMessage={t('dashboard.storePerformance.selectCompanyFirst')}
                searchPlaceholder={t('dashboard.storePerformance.search')}
                selectAllLabel={t('dashboard.storePerformance.selectAll')}
                icons={icons}
                selectionType={questionnaireSelectionType}
                styles={{
                  listColumn: styles.listColumn,
                  listColumnHeader: styles.listColumnHeader,
                  listColumnCounter: styles.listColumnCounter,
                  searchField: styles.searchField,
                  checkboxRow: styles.checkboxRow,
                  checkboxRowAll: styles.checkboxRowAll,
                }}
                colors={{ color9: colors.color9, color13: colors.color13 }}
                t={t}
              />
            </Grid>
          </Grid>

          {hasSelectedQuestionnaireWithVersions ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '16px' }}>
                <Button
                  variant="contained"
                  disabled={!canConsult}
                  onClick={openVersionsPanel}
                  sx={{
                    ...styles.consultButton,
                    backgroundColor: canConsult ? colors.color45 : colors.color10,
                    color: colors.color5,
                    '&:hover': {
                      backgroundColor: canConsult ? `${colors.color16}dd` : colors.color10,
                    },
                  }}
                >
                  {t('dashboard.storePerformance.consultExecutions')}
                </Button>
              </Box>

              {showVersionsPanel && (
                <>
                  <VersionSelectionPanel
                    queryMode={queryMode}
                    selectedQuestionnairesWithVersions={selectedQuestionnairesWithVersions}
                    selectedVersionsByQuestionnaire={selectedVersionsByQuestionnaire}
                    activeQuestionnaireId={activeVersionQuestionnaireId}
                    onSelectQuestionnaire={setActiveVersionQuestionnaire}
                    onToggleVersion={toggleVersionSelection}
                    onToggleAllVersions={toggleAllVersionsForQuestionnaire}
                    t={t}
                    colors={{ color9: colors.color9, color13: colors.color13, color45: colors.color45 }}
                  />
                  <Box sx={styles.footerCard}>
                    {!hasQueried && (
                      <Box sx={styles.infoText}>
                        {icons('info-outlined')}
                        <Typography sx={{ fontSize: '13px', color: colors.color9 }}>
                          {t('dashboard.storePerformance.selectAtLeastOneUnit')}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginLeft: 'auto' }}>
                      {lastConsultTimeFormatted && (
                        <Typography sx={{ fontSize: '13px', color: colors.color9, whiteSpace: 'nowrap' }}>
                          {t('dashboard.storePerformance.updatedAt')} {lastConsultTimeFormatted}
                        </Typography>
                      )}
                      <Button
                        variant="contained"
                        disabled={!canViewResults}
                        onClick={handleConsult}
                        sx={{
                          ...styles.consultButton,
                          backgroundColor: canViewResults ? colors.color45 : colors.color10,
                          color: colors.color5,
                          '&:hover': {
                            backgroundColor: canViewResults ? `${colors.color16}dd` : colors.color10,
                          },
                        }}
                      >
                        {t('dashboard.storePerformance.consultResults')}
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </>
          ) : (
            <Box sx={styles.footerCard}>
              {!hasQueried && (
                <Box sx={styles.infoText}>
                  {icons('info-outlined')}
                  <Typography sx={{ fontSize: '13px', color: colors.color9 }}>
                    {t('dashboard.storePerformance.selectAtLeastOneUnit')}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginLeft: 'auto' }}>
                {lastConsultTimeFormatted && (
                  <Typography sx={{ fontSize: '13px', color: colors.color9, whiteSpace: 'nowrap' }}>
                    {t('dashboard.storePerformance.updatedAt')} {lastConsultTimeFormatted}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  disabled={!canConsult}
                  onClick={handleConsult}
                  sx={{
                    ...styles.consultButton,
                    backgroundColor: canConsult ? colors.color45 : colors.color10,
                    color: colors.color5,
                    '&:hover': {
                      backgroundColor: canConsult ? `${colors.color16}dd` : colors.color10,
                    },
                  }}
                >
                  {t('dashboard.storePerformance.consultResults')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Results area */}
        <QueryResults
          hasQueried={hasQueried}
          resultsLoading={resultsLoading}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          selectedResultIds={selectedResultIds}
          toggleResultItem={toggleResultItem}
          selectAllResultItems={selectAllResultItems}
          searchResult={searchResult}
          setSearchResult={setSearchResult}
          paginatedResultItems={paginatedResultItems}
          filteredResultItems={filteredResultItems}
          performanceData={performanceData}
          resultsPage={resultsPage}
          setResultsPage={setResultsPage}
          totalPages={totalPages}
          resultListTitle={resultListTitle}
          chartType={chartType}
          onChartTypeChange={handleChartTypeChange}
          chartData={chartData}
          chartSeries={chartSeries}
          globalAverageScore={globalAverageScore}
          chartColors={chartColors}
          pieChartData={filteredPieChartData}
          pieChartLoading={pieChartLoading}
          pieChartPage={pieChartPagination.currentPage}
          pieChartTotalPages={pieChartPagination.totalPages}
          onPieChartPageChange={handlePieChartPageChange}
          queryMode={queryMode}
          processStatusQueryParams={processStatusQueryParams}
          scoreMap={scoreMap}
          onRangeApplied={handleRangeApplied}
        />
      </Box>

      {/* Filter Modal */}
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        periodFrom={periodFrom}
        setPeriodFrom={setPeriodFrom}
        periodTo={periodTo}
        setPeriodTo={setPeriodTo}
        periodWeek={periodWeek}
        setPeriodWeek={setPeriodWeek}
        periodYear={periodYear}
        setPeriodYear={setPeriodYear}
        periodValidationError={periodValidationError}
        hasPeriodErrors={hasPeriodErrors}
        filterDivision={filterDivision}
        setFilterDivision={setFilterDivision}
        filterZone={filterZone}
        setFilterZone={setFilterZone}
        filterUser={filterUser}
        setFilterUser={setFilterUser}
        filterRole={filterRole}
        setFilterRole={setFilterRole}
        divisionsOptions={divisionsOptions}
        zonesOptions={zonesOptions}
        usersOptions={usersOptions}
        rolesOptions={rolesOptions}
        filtersLoading={filtersLoading}
        isPartner={isPartner}
        hideZone={hideZone}
        onApply={() => {
          handleApplyFilters()
          setFilterModalOpen(false)
        }}
        styles={{
          filterDrawerTitle: styles.filterDrawerTitle,
          filterSectionTitle: styles.filterSectionTitle,
          filterPeriodRow: styles.filterPeriodRow,
          filterField: styles.filterField,
          filterButtonContainer: styles.filterButtonContainer,
          filterApplyButton: styles.filterApplyButton,
        }}
        t={t}
      />

      <SnackSimple
        open={snackbar.open}
        close={closeSnackbar}
        message={t(snackbar.message, snackbar.interpolationOptions)}
        type={snackbar.type as any}
      />

      <Popover
        open={downloadPopoverOpen}
        anchorEl={downloadPopoverAnchor}
        onClose={closeDownloadPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={styles.buttons}>
          <IconButton
            className='red flex-col'
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
          >
            {icons('pdf')}
            <Typography variant='footer'>{t('general.pdf')}</Typography>
          </IconButton>
          <IconButton
            className='green flex-col'
            onClick={handleDownloadExcel}
            disabled={downloadingExcel}
          >
            {icons('csv')}
            <Typography variant='footer'>{t('general.csv')}</Typography>
          </IconButton>
        </Box>
      </Popover>
    </Grid>
    </StorePerformanceProvider>
  )
}

export { StorePerformance }
